import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock, Transition as _Transition, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  "data-cy": "settlement-settings-modal",
  class: "settlement-settings-panel fixed top-[6rem] right-0 bg-white shadow-2xl z-[200] flex flex-col rounded-l-lg overflow-hidden"
}
const _hoisted_2 = { class: "flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white shrink-0" }
const _hoisted_3 = { class: "text-subtitle-1 font-weight-bold text-grey-darken-3 flex items-center gap-2" }
const _hoisted_4 = {
  key: 0,
  class: "flex items-center gap-1.5 px-5 py-2 bg-[rgba(var(--v-theme-error),0.08)] border-b border-[rgba(var(--v-theme-error),0.2)] text-caption text-error shrink-0"
}
const _hoisted_5 = {
  key: 1,
  class: "flex items-center gap-1.5 px-5 py-2 bg-orange-50 border-b border-orange-100 text-caption text-orange-darken-2 shrink-0"
}
const _hoisted_6 = { class: "flex-1 overflow-y-auto" }
const _hoisted_7 = { class: "flex items-center gap-1 text-body-2 font-weight-medium text-grey-darken-2 mb-1.5" }
const _hoisted_8 = { class: "flex items-center gap-1 text-body-2 font-weight-medium text-grey-darken-2 mb-1.5" }
const _hoisted_9 = { class: "text-caption text-grey-darken-1 mt-1.5" }
const _hoisted_10 = { class: "flex items-center gap-1 text-body-2 font-weight-medium text-grey-darken-2 mb-1.5" }
const _hoisted_11 = { class: "text-caption text-grey-darken-1 mt-1.5" }
const _hoisted_12 = { class: "flex items-center gap-1 text-body-2 font-weight-medium text-grey-darken-2 mb-1.5" }
const _hoisted_13 = { class: "flex items-center gap-1 text-body-2 font-weight-medium text-grey-darken-2 mb-1.5" }
const _hoisted_14 = { class: "flex items-center gap-1 text-body-2 font-weight-medium text-grey-darken-2 mb-1.5" }
const _hoisted_15 = { class: "text-caption text-grey-darken-1 mt-1.5" }
const _hoisted_16 = { key: 1 }
const _hoisted_17 = { class: "flex items-center justify-between" }
const _hoisted_18 = { class: "flex items-center gap-1 text-body-2 font-weight-medium text-grey-darken-2" }
const _hoisted_19 = { class: "flex items-center gap-1 text-body-2 font-weight-medium text-grey-darken-2 mb-1.5" }
const _hoisted_20 = {
  key: 2,
  class: "flex items-center justify-between px-5 py-4 border-t border-gray-200 bg-white shrink-0 mr-16"
}

import { ref, reactive, computed, nextTick } from 'vue';
import type { PropType } from 'vue';
import { format, isValid, parseISO } from 'date-fns';
import { parseLocalDate, yyyymmdddash } from '@/services/utils';
import LlTextField from '@/components/LlTextField.vue';
import LlSelect from '@/components/LlSelect.vue';
import LlDatePicker from '@/components/LlDatePicker.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import LlSwitch from '@/components/LlSwitch.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlIcon from '@/components/LlIcon.vue';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import SkeletonRenderer from '@/components/SkeletonRenderer.vue';
import { FEE_METHOD, EXPENSE_ARRANGEMENT } from '@/constants/settlementConstants';
import type { FeeMethod, ExpenseArrangement } from '@/models/settlementTypes';

interface SettingsForm {
  feePercentage: number | null
  feeMethod: FeeMethod
  expenseArrangement: ExpenseArrangement
  settlementDate: string | null
}

interface SettlementSettingsSavePayload {
  settings: SettingsForm
  noExpenseInterest: boolean
  expenseFromDate: string | null
  caseStatus: string | null
}

type SkeletonItem = { type: 'paragraph' | 'list-item'; class?: string }


export default /*@__PURE__*/_defineComponent({
  __name: 'SettlementSettingsModal',
  props: {
  loading: { type: Boolean, default: false },
  /** Last-saved values — read once as the draft's seed each time `open()` is called.
   *  Does NOT reflect in-progress edits; the modal owns its own draft internally. */
  settingsForm: { type: Object as PropType<SettingsForm>, required: true },
  isLocked: { type: Boolean, default: false },
  canReset: { type: Boolean, default: true },
  resetBlockedReason: { type: String, default: null },
  companyExpenseInterestRate: { type: Number, default: null },
  /** Last-saved value — seed only, see `settingsForm`. */
  noExpenseInterest: { type: Boolean, default: false },
  /** Last-saved value — seed only, see `settingsForm`. YYYY-MM-DD string. */
  expenseFromDate: { type: String as () => string | null, default: null },
  /** Last-saved value — seed only, see `settingsForm`. Matter-level field (not part of
   *  the Settlement record) — same 8-value enum MatterInfo.vue's Case Status dropdown
   *  reads/writes. */
  caseStatus: { type: String as () => string | null, default: null },
  caseStatusOptions: { type: Array as PropType<Array<{ value: string; label: string }>>, default: () => [] },
  /** Gate the interest-rate / toggle / as-of-date fields (sections 5–7) behind the
   *  settlementRedesign flag. When false, interest is managed inline on Step 2. */
  redesignEnabled: { type: Boolean, default: false },
  /** True while the parent is persisting a Save — disables the footer buttons. */
  saving: { type: Boolean, default: false },
},
  emits: ["save"],
  setup(__props, { expose: __expose, emit: __emit }) {

const SKELETON_FIELD_WIDTHS = ['w-1/2', 'w-2/3', 'w-1/2', 'w-1/3', 'w-2/5', 'w-1/3', 'w-1/3'] as const

const skeletonSpec: SkeletonItem[] = SKELETON_FIELD_WIDTHS.flatMap((width, i, arr) => [
  { type: 'paragraph', class: `mb-1 ${width}` },
  { type: 'list-item', class: i < arr.length - 1 ? 'mb-5' : undefined },
])

const props = __props;

const emit = __emit;

// ── Visibility + draft (SC-45774 follow-up: owned internally, not threaded through the parent) ──
const isOpen = ref(false);
const draft = reactive<SettingsForm>({
  feePercentage: null,
  feeMethod: FEE_METHOD.GROSS,
  expenseArrangement: EXPENSE_ARRANGEMENT.FIRM,
  settlementDate: null,
});
const draftNoExpenseInterest = ref(false);
const draftExpenseFromDate = ref<string | null>(null);
const draftCaseStatus = ref<string | null>(null);
const showDiscardChangesDialog = ref(false);

function seedDraft() {
  draft.feePercentage = props.settingsForm.feePercentage;
  draft.feeMethod = props.settingsForm.feeMethod;
  draft.expenseArrangement = props.settingsForm.expenseArrangement;
  draft.settlementDate = props.settingsForm.settlementDate;
  draftNoExpenseInterest.value = props.noExpenseInterest;
  draftExpenseFromDate.value = props.expenseFromDate;
  draftCaseStatus.value = props.caseStatus;
}

function isDirty(): boolean {
  return draft.feePercentage !== props.settingsForm.feePercentage
    || draft.feeMethod !== props.settingsForm.feeMethod
    || draft.expenseArrangement !== props.settingsForm.expenseArrangement
    || draft.settlementDate !== props.settingsForm.settlementDate
    || draftNoExpenseInterest.value !== props.noExpenseInterest
    || draftExpenseFromDate.value !== props.expenseFromDate
    || draftCaseStatus.value !== props.caseStatus;
}

function discardDraft() {
  isOpen.value = false;
}

function onCancelClick() {
  if (isDirty()) {
    showDiscardChangesDialog.value = true;
  } else {
    discardDraft();
  }
}

function confirmDiscardChanges() {
  showDiscardChangesDialog.value = false;
  discardDraft();
}

function cancelDiscardChanges() {
  showDiscardChangesDialog.value = false;
  // Stay open — draft untouched.
}

const formRef = ref<{ validate(): Promise<{ valid: boolean }> } | null>(null);

const settingsLocked = computed(() => props.isLocked || !props.canReset);

// ── Options ──────────────────────────────────────────────────
// 'Net of Expenses' + 'Firm absorbs expenses (from fee)' is not a supported
// combination — expenses can't be deducted from both the recovery (Net) and
// the fee (Absorbed) at once. Each side disables the option that would create it.
const feeMethodOptions = computed(() => [
  { label: 'Gross — fee calculated on full settlement amount', value: FEE_METHOD.GROSS },
  {
    label: 'Net of Expenses — fee calculated after deducting costs',
    value: FEE_METHOD.NET,
    disabled: draft.expenseArrangement === EXPENSE_ARRANGEMENT.ABSORBED,
  },
]);

const expenseArrangementOptions = computed(() => [
  { label: 'Firm advances all expenses', value: EXPENSE_ARRANGEMENT.FIRM },
  { label: 'Client pays expenses as they go', value: EXPENSE_ARRANGEMENT.CLIENT },
  {
    label: 'Firm absorbs expenses (from fee)',
    value: EXPENSE_ARRANGEMENT.ABSORBED,
    disabled: draft.feeMethod === FEE_METHOD.NET,
  },
]);

// ── Validation ───────────────────────────────────────────────
const feePercentageRules = [
  (v: number | null | string) => {
    const msg = 'Enter a number between 0 and 99 with up to 8 decimals';
    if (v == null || v === '') return true;
    const num = Number(v);
    if (Number.isNaN(num)) return msg;
    if (num < 0 || num >= 100) return msg;
    if (num !== 0 && num < 0.00000001) return msg;
    const parts = String(v).split('.');
    if (parts[1] && parts[1].length > 8) return msg;
    return true;
  },
];

async function onSaveClick() {
  const result = await formRef.value?.validate();
  if (result?.valid !== true) return;
  emit('save', {
    settings: {
      feePercentage: draft.feePercentage,
      feeMethod: draft.feeMethod,
      expenseArrangement: draft.expenseArrangement,
      settlementDate: draft.settlementDate,
    },
    noExpenseInterest: draftNoExpenseInterest.value,
    expenseFromDate: draftExpenseFromDate.value,
    caseStatus: draftCaseStatus.value,
  });
}

/** `Number('')` is `0`, not NaN — coercing blindly would silently turn a cleared
 *  field into a real 0% fee instead of the "unset" state feePercentageRules treats
 *  empty input as. */
function onFeePercentageInput(value: string | number | null) {
  draft.feePercentage = value === '' || value == null ? null : Number(value);
}

// ── Hints ────────────────────────────────────────────────────
const feeMethodHint = computed(() => {
  if (draft.expenseArrangement === EXPENSE_ARRANGEMENT.ABSORBED) {
    return "Attorney fee is calculated on the full recovery amount. 'Net of Expenses' isn't available while expenses are absorbed by the firm.";
  }
  if (draft.feeMethod === FEE_METHOD.GROSS) return 'Attorney fee is calculated on the full recovery amount.';
  if (draft.feeMethod === FEE_METHOD.NET) return 'Attorney fee is calculated after deducting costs from the recovery amount.';
  return 'Select a fee calculation method.';
});

const expenseArrangementHint = computed(() => {
  if (draft.feeMethod === FEE_METHOD.NET) {
    return "The firm fronts all costs and is reimbursed from the settlement. 'Firm absorbs expenses' isn't available with 'Net of Expenses' fee calculation.";
  }
  if (draft.expenseArrangement === EXPENSE_ARRANGEMENT.FIRM) return 'The firm fronts all costs and is reimbursed from the settlement.';
  if (draft.expenseArrangement === EXPENSE_ARRANGEMENT.ABSORBED) return 'Expenses come out of the attorney fee. The client pays no costs.';
  return 'Determines whether prior client payments appear on the settlement statement.';
});

// ── Settlement Date ──────────────────────────────────────────
const settlementDateModel = computed<Date | null>(() => {
  const dateStr = draft.settlementDate;
  if (!dateStr) return null;
  const parsed = parseISO(dateStr);
  return isValid(parsed) ? parsed : null;
});

function onSettlementDateChange(value: Date | null) {
  if (!value) return;
  const formatted = format(value, 'yyyy-MM-dd');
  if (formatted === draft.settlementDate) return;
  draft.settlementDate = formatted;
}

// ── Expense Interest Rate (read-only display) ────────────────
const expenseInterestDisplay = computed(() => {
  const rate = props.companyExpenseInterestRate;
  if (rate == null || rate === 0) return '0% — none';
  return `${rate}%`;
});

// ── Expense Interest toggle ──────────────────────────────────
// Mirrors Angular: expenseInterest = !noExpenseInterest
const expenseInterestEnabled = computed(() => !draftNoExpenseInterest.value)

const isFirmArrangement = computed(() => draft.expenseArrangement === EXPENSE_ARRANGEMENT.FIRM);

function onExpenseInterestToggle(enabled: boolean) {
  draftNoExpenseInterest.value = !enabled;
}

// ── Interest As-Of Date ──────────────────────────────────────
const expenseFromDateModel = computed<Date | null>(() => parseLocalDate(draftExpenseFromDate.value));

function onExpenseFromDateChange(val: Date | null) {
  const next = yyyymmdddash(val) || null;
  if (next === draftExpenseFromDate.value) return;
  draftExpenseFromDate.value = next;
}

// ── Field focus (used internally by open()) ───────────────────
const feePercentageContainer = ref<HTMLElement | null>(null)
const feeMethodContainer = ref<HTMLElement | null>(null)
const expenseArrangementContainer = ref<HTMLElement | null>(null)
const settlementDateContainer = ref<HTMLElement | null>(null)
const expenseInterestContainer = ref<HTMLElement | null>(null)
const expenseInterestDateContainer = ref<HTMLElement | null>(null)
const caseStatusContainer = ref<HTMLElement | null>(null)

const fieldContainers: Record<string, ReturnType<typeof ref<HTMLElement | null>>> = {
  feePercentage: feePercentageContainer,
  feeMethod: feeMethodContainer,
  expenseArrangement: expenseArrangementContainer,
  settlementDate: settlementDateContainer,
  expenseInterest: expenseInterestContainer,
  expenseInterestDate: expenseInterestDateContainer,
  caseStatus: caseStatusContainer,
}

function focusFieldInternal(key: string) {
  const el = fieldContainers[key]?.value
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  el.querySelector<HTMLElement>('input, [role="combobox"]')?.focus()
}

/** Opens the panel (seeding the draft from current props) unless it's already open, in
 *  which case an in-progress edit is left untouched. Either way, scrolls/focuses
 *  `focusField` if given. Unifies what used to be three parent-side functions
 *  (openSettingsPanel/openSettingsAndFocus/openSettingsDraft). */
function open(focusField?: string) {
  if (!isOpen.value) {
    seedDraft();
    isOpen.value = true;
  }
  if (focusField) {
    nextTick(() => focusFieldInternal(focusField));
  }
}

function close() {
  isOpen.value = false;
}

__expose({ open, close });

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_Transition, {
      name: "settlement-settings-modal",
      "enter-active-class": "settlement-settings-modal-enter-active",
      "leave-active-class": "settlement-settings-modal-leave-active",
      "enter-from-class": "settlement-settings-modal-enter-from",
      "enter-to-class": "settlement-settings-modal-enter-to",
      "leave-from-class": "settlement-settings-modal-leave-from",
      "leave-to-class": "settlement-settings-modal-leave-to"
    }, {
      default: _withCtx(() => [
        (isOpen.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("h2", _hoisted_3, [
                  _createVNode(_component_v_icon, {
                    size: "18",
                    color: "grey-darken-1"
                  }, {
                    default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                      _createTextVNode("mdi-cog", -1)
                    ]))]),
                    _: 1
                  }),
                  _cache[5] || (_cache[5] = _createTextVNode(" Settlement Settings ", -1))
                ]),
                _createVNode(LlSecondaryButton, {
                  icon: "mdi-close",
                  variant: "text",
                  size: "small",
                  "aria-label": "Close settlement settings",
                  disabled: __props.saving,
                  "data-cy": "settlement-settings-modal-close",
                  onClick: onCancelClick
                }, null, 8, ["disabled"])
              ]),
              (!__props.canReset && __props.resetBlockedReason)
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _createVNode(LlIcon, {
                      icon: "mdi-lock",
                      size: "14",
                      color: "error"
                    }),
                    _createElementVNode("span", null, _toDisplayString(__props.resetBlockedReason), 1)
                  ]))
                : (settingsLocked.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                      _createVNode(_component_v_icon, {
                        size: "14",
                        color: "orange-darken-2"
                      }, {
                        default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                          _createTextVNode("mdi-lock", -1)
                        ]))]),
                        _: 1
                      }),
                      _cache[7] || (_cache[7] = _createElementVNode("span", null, "Settings locked · frozen after statement", -1))
                    ]))
                  : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_6, [
                (__props.loading)
                  ? (_openBlock(), _createBlock(SkeletonRenderer, {
                      key: 0,
                      spec: _unref(skeletonSpec),
                      "container-class": "px-5 py-5 space-y-0"
                    }, null, 8, ["spec"]))
                  : (_openBlock(), _createBlock(_component_v_form, {
                      key: 1,
                      ref_key: "formRef",
                      ref: formRef,
                      class: "px-5 py-5 space-y-6"
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", {
                          ref_key: "feePercentageContainer",
                          ref: feePercentageContainer
                        }, [
                          _createElementVNode("div", _hoisted_7, [
                            _cache[8] || (_cache[8] = _createTextVNode(" Contingency Fee % ", -1)),
                            _createVNode(LlTooltip, {
                              icon: "info",
                              text: "Configured per the matter's fee agreement."
                            })
                          ]),
                          _createVNode(LlTextField, {
                            "model-value": draft.feePercentage,
                            type: "number",
                            suffix: "%",
                            rules: feePercentageRules,
                            "hide-details": false,
                            density: "compact",
                            variant: "outlined",
                            disabled: settingsLocked.value,
                            "onUpdate:modelValue": onFeePercentageInput
                          }, null, 8, ["model-value", "disabled"])
                        ], 512),
                        _createElementVNode("div", {
                          ref_key: "feeMethodContainer",
                          ref: feeMethodContainer
                        }, [
                          _createElementVNode("div", _hoisted_8, [
                            _cache[9] || (_cache[9] = _createTextVNode(" Fee Calculation Method ", -1)),
                            _createVNode(LlTooltip, {
                              icon: "info",
                              text: "Attorney fee is calculated on the full recovery amount."
                            })
                          ]),
                          _createVNode(LlSelect, {
                            "model-value": draft.feeMethod,
                            items: feeMethodOptions.value,
                            "item-title": "label",
                            "item-value": "value",
                            "item-props": (item) => ({ disabled: item.disabled }),
                            "return-object": false,
                            variant: "outlined",
                            density: "compact",
                            "hide-details": "",
                            disabled: settingsLocked.value,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (draft.feeMethod = $event))
                          }, null, 8, ["model-value", "items", "item-props", "disabled"]),
                          _createElementVNode("p", _hoisted_9, _toDisplayString(feeMethodHint.value), 1)
                        ], 512),
                        _createElementVNode("div", {
                          ref_key: "expenseArrangementContainer",
                          ref: expenseArrangementContainer
                        }, [
                          _createElementVNode("div", _hoisted_10, [
                            _cache[10] || (_cache[10] = _createTextVNode(" Expense Arrangement ", -1)),
                            _createVNode(LlTooltip, {
                              icon: "info",
                              text: "Determines how case expenses are treated in the settlement calculation."
                            })
                          ]),
                          _createVNode(LlSelect, {
                            "model-value": draft.expenseArrangement,
                            items: expenseArrangementOptions.value,
                            "item-title": "label",
                            "item-value": "value",
                            "item-props": (item) => ({ disabled: item.disabled }),
                            "return-object": false,
                            variant: "outlined",
                            density: "compact",
                            "hide-details": "",
                            disabled: settingsLocked.value,
                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (draft.expenseArrangement = $event))
                          }, null, 8, ["model-value", "items", "item-props", "disabled"]),
                          _createElementVNode("p", _hoisted_11, _toDisplayString(expenseArrangementHint.value), 1)
                        ], 512),
                        _createElementVNode("div", {
                          ref_key: "settlementDateContainer",
                          ref: settlementDateContainer
                        }, [
                          _createElementVNode("div", _hoisted_12, [
                            _cache[11] || (_cache[11] = _createTextVNode(" Settlement Date ", -1)),
                            _createVNode(LlTooltip, {
                              icon: "info",
                              text: "Shown on the settlement statement."
                            })
                          ]),
                          _createVNode(LlDatePicker, {
                            "model-value": settlementDateModel.value,
                            label: "",
                            disabled: settingsLocked.value,
                            "hide-details": true,
                            "data-cy": "settlement-date-picker",
                            "onUpdate:modelValue": onSettlementDateChange
                          }, null, 8, ["model-value", "disabled"])
                        ], 512),
                        _createElementVNode("div", {
                          ref_key: "caseStatusContainer",
                          ref: caseStatusContainer
                        }, [
                          _createElementVNode("div", _hoisted_13, [
                            _cache[12] || (_cache[12] = _createTextVNode(" Case Status ", -1)),
                            _createVNode(LlTooltip, {
                              icon: "info",
                              text: "Where this matter stands in the litigation lifecycle."
                            })
                          ]),
                          _createVNode(LlSelect, {
                            "model-value": draftCaseStatus.value,
                            items: __props.caseStatusOptions,
                            "item-title": "label",
                            "item-value": "label",
                            "return-object": false,
                            variant: "outlined",
                            density: "compact",
                            "hide-details": "",
                            clearable: "",
                            placeholder: "Select Case Status",
                            disabled: settingsLocked.value,
                            "data-cy": "settlement-case-status-select",
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (draftCaseStatus.value = $event))
                          }, null, 8, ["model-value", "items", "disabled"])
                        ], 512),
                        (__props.redesignEnabled)
                          ? (_openBlock(), _createElementBlock("div", {
                              key: 0,
                              ref_key: "expenseInterestContainer",
                              ref: expenseInterestContainer
                            }, [
                              _createElementVNode("div", _hoisted_14, [
                                _cache[13] || (_cache[13] = _createTextVNode(" Expense Interest Rate ", -1)),
                                _createVNode(LlTooltip, {
                                  icon: "info",
                                  text: draftNoExpenseInterest.value
                  ? 'Interest has been disabled for this matter.'
                  : 'Expense interest rate configured at the firm level.'
                                }, null, 8, ["text"])
                              ]),
                              _createVNode(LlTextField, {
                                "model-value": expenseInterestDisplay.value,
                                disabled: "",
                                variant: "outlined",
                                density: "compact",
                                "hide-details": ""
                              }, null, 8, ["model-value"]),
                              _createElementVNode("p", _hoisted_15, _toDisplayString(draftNoExpenseInterest.value ? 'Interest is disabled for this matter.' : 'Configured at the firm level in Settings › Firm Setup › Billing.'), 1)
                            ], 512))
                          : _createCommentVNode("", true),
                        (__props.companyExpenseInterestRate)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                              _createElementVNode("div", _hoisted_17, [
                                _createElementVNode("div", _hoisted_18, [
                                  _cache[14] || (_cache[14] = _createTextVNode(" Expense Interest ", -1)),
                                  _createVNode(LlTooltip, {
                                    icon: "info",
                                    text: isFirmArrangement.value
                    ? 'The interest is set in Settings > Firm Setup > Billing - when enabled for the client it will show up on invoices'
                    : 'Expense interest can only be calculated for matters with the expense arrangement \'Firm advances all expenses\''
                                  }, null, 8, ["text"])
                                ]),
                                _createVNode(LlSwitch, {
                                  "model-value": expenseInterestEnabled.value,
                                  disabled: !isFirmArrangement.value,
                                  color: "primary",
                                  "hide-details": "",
                                  "onUpdate:modelValue": onExpenseInterestToggle
                                }, null, 8, ["model-value", "disabled"])
                              ])
                            ]))
                          : _createCommentVNode("", true),
                        (__props.redesignEnabled && __props.companyExpenseInterestRate && isFirmArrangement.value && expenseInterestEnabled.value)
                          ? (_openBlock(), _createElementBlock("div", {
                              key: 2,
                              ref_key: "expenseInterestDateContainer",
                              ref: expenseInterestDateContainer
                            }, [
                              _createElementVNode("div", _hoisted_19, [
                                _cache[15] || (_cache[15] = _createTextVNode(" Interest As-Of Date ", -1)),
                                _createVNode(LlTooltip, {
                                  icon: "info",
                                  text: "The end date for interest accrual. Expenses dated after this date accrue 0 days of interest."
                                })
                              ]),
                              _createVNode(LlDatePicker, {
                                "model-value": expenseFromDateModel.value,
                                label: "",
                                "hide-details": true,
                                "onUpdate:modelValue": onExpenseFromDateChange
                              }, null, 8, ["model-value"]),
                              _cache[16] || (_cache[16] = _createElementVNode("p", { class: "text-caption text-grey-darken-1 mt-1.5" }, "Defaults to today's date.", -1))
                            ], 512))
                          : _createCommentVNode("", true)
                      ]),
                      _: 1
                    }, 512))
              ]),
              (!__props.loading)
                ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                    _createVNode(LlSecondaryButton, {
                      "data-cy": "settlement-settings-modal-cancel",
                      disabled: __props.saving,
                      onClick: onCancelClick
                    }, {
                      default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                        _createTextVNode(" Cancel ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["disabled"]),
                    _createVNode(LlSecondaryButton, {
                      variant: "flat",
                      color: "primary",
                      "data-cy": "settlement-settings-modal-save",
                      disabled: settingsLocked.value || __props.saving,
                      loading: __props.saving,
                      onClick: onSaveClick
                    }, {
                      default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                        _createTextVNode(" Save ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["disabled", "loading"])
                  ]))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(LlConfirmationDialog, {
      modelValue: showDiscardChangesDialog.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((showDiscardChangesDialog).value = $event)),
      title: "Discard Changes?",
      message: "You have unsaved changes to these settings. If you close now, your edits will be lost.",
      "confirm-text": "Discard Changes",
      "toggle-text": "Keep Editing",
      onConfirm: confirmDiscardChanges,
      onCancel: cancelDiscardChanges
    }, null, 8, ["modelValue"])
  ], 64))
}
}

})